import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Flag from "react-flagkit";

export const countryCodes = [
  { code: "+1", country: "US", label: "United States" },
  { code: "+44", country: "GB", label: "United Kingdom" },
  { code: "+91", country: "IN", label: "India" },
  { code: "+61", country: "AU", label: "Australia" },
  { code: "+81", country: "JP", label: "Japan" },
  { code: "+49", country: "DE", label: "Germany" },
  { code: "+33", country: "FR", label: "France" },
  { code: "+39", country: "IT", label: "Italy" },
  { code: "+34", country: "ES", label: "Spain" },
  { code: "+55", country: "BR", label: "Brazil" },
  { code: "+86", country: "CN", label: "China" },
  { code: "+82", country: "KR", label: "South Korea" },
  { code: "+52", country: "MX", label: "Mexico" },
  { code: "+7", country: "RU", label: "Russia" },
  { code: "+27", country: "ZA", label: "South Africa" },
  { code: "+966", country: "SA", label: "Saudi Arabia" },
  { code: "+971", country: "AE", label: "UAE" },
  { code: "+65", country: "SG", label: "Singapore" },
  { code: "+60", country: "MY", label: "Malaysia" },
  { code: "+64", country: "NZ", label: "New Zealand" },
];

export const AlreadySentPopup = ({ onClose, t = {} }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    const timer = setTimeout(() => {
      onClose();
    }, 12000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  return createPortal(
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm cursor-pointer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] rounded-[27px] border border-[rgba(199,191,229,0.20)] px-8 py-10 flex flex-col items-center gap-6 w-full max-w-[400px] mx-4 shadow-2xl cursor-default"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-5 text-white/60 hover:text-white text-2xl leading-none bg-transparent border-none cursor-pointer"
          aria-label="Close"
        >
          ×
        </button>
        <div className="w-16 h-16 rounded-full bg-[#904DB0]/30 flex items-center justify-center">
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="18" fill="#904DB0" fillOpacity="0.3" />
            <path d="M10 18l6 6 10-12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="text-center">
          <h2 className="text-white font-extrabold text-[24px] leading-tight mb-2">
            {t.contactUsSuccessTitle || "Request Sent!"}
          </h2>
          <p className="text-[#D2C5E8] text-[15px] font-light">
            {t.contactUsSuccessDesc || "We'll contact you as soon as possible."}
          </p>
        </div>
        <button
          onClick={onClose}
          className="w-full h-[50px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] transition-colors text-[16px]"
        >
          {t.paymentClose || "Close"}
        </button>
      </div>
    </div>,
    document.body
  );
};

export const ContactUsPopup = ({ onClose, t = {}, onSubmit, isSubmitting, isSuccess, prefilledEmail = "" }) => {
  const [form, setForm] = useState({
    name: "",
    email: prefilledEmail || "",
    phone_no: "",
    country_code: "+1",
    note: "",
  });
  const [errors, setErrors] = useState({});
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleEsc = (e) => {
      if (e.key === "Escape" && !isSubmitting) onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [onClose, isSubmitting]);

  useEffect(() => {
    if (!isSuccess) return;
    const timer = setTimeout(() => {
      onClose();
    }, 12000);
    return () => clearTimeout(timer);
  }, [isSuccess, onClose]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t.contactUsNameReq || "Name is required";
    if (!form.email.trim()) errs.email = t.contactUsEmailReq || "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = t.contactUsEmailInvalid || "Enter a valid email";
    const phoneDigits = form.phone_no.replace(/\D/g, "");
    if (!form.phone_no.trim()) errs.phone_no = t.phoneRequired || "Phone number is required";
    else if (phoneDigits.length < 7 || phoneDigits.length > 15)
      errs.phone_no = t.phoneDigits || "Phone number must be between 7 and 15 digits";
    else if (!/^[\d\s\-+()]+$/.test(form.phone_no))
      errs.phone_no = t.phoneInvalid || "Enter a valid phone number";
    if (!form.country_code.trim()) errs.country_code = t.countryCodeRequired || "Country code is required";
    if (!form.note.trim()) errs.note = t.contactUsNoteReq || "Note is required";
    return errs;
  };

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    if (name === "phone_no") {
      value = value.replace(/\D/g, "").slice(0, 15);
    }
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "phone_no") {
      const digits = value.replace(/\D/g, "");
      if (value.trim() === "") {
        setErrors((prev) => ({ ...prev, phone_no: t.phoneRequired || "Phone number is required" }));
      } else if (!/^\+?[\d\s\-()]+$/.test(value)) {
        setErrors((prev) => ({ ...prev, phone_no: t.phoneInvalid || "Enter a valid phone number" }));
      } else if (digits.length < 7 || digits.length > 15) {
        setErrors((prev) => ({ ...prev, phone_no: t.phoneDigits || "Phone number must be between 7 and 15 digits" }));
      } else {
        setErrors((prev) => ({ ...prev, phone_no: undefined }));
      }
    } else {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCountrySelect = (code) => {
    setForm((prev) => ({ ...prev, country_code: code }));
    setShowCountryDropdown(false);
    setErrors((prev) => ({ ...prev, country_code: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSubmit(form);
  };

  const selectedCountry =
    countryCodes.find((c) => c.code === form.country_code) || countryCodes[0];

  if (isSuccess) {
    return createPortal(
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm cursor-pointer"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] rounded-[27px] border border-[rgba(199,191,229,0.20)] px-8 py-10 flex flex-col items-center gap-6 w-full max-w-[440px] mx-4 shadow-2xl cursor-default"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-5 text-white/60 hover:text-white text-2xl leading-none bg-transparent border-none cursor-pointer"
            aria-label="Close"
          >
            ×
          </button>
          <div className="w-16 h-16 rounded-full bg-[#904DB0]/30 flex items-center justify-center">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <circle cx="18" cy="18" r="18" fill="#904DB0" fillOpacity="0.3" />
              <path d="M10 18l6 6 10-12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-center">
            <h2 className="text-white font-extrabold text-[24px] leading-tight mb-2">
              {t.contactReqSent || "Request Sent!"}
            </h2>
            <p className="text-white text-[15px] font-light">
              {t.contactReqSentDesc || "We'll contact you as soon as possible."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-full h-[50px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] transition-colors text-[16px]"
          >
            {t.contactClose || "Close"}
          </button>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm cursor-pointer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] rounded-[27px] border border-[rgba(199,191,229,0.20)] px-8 py-10 flex flex-col gap-6 w-full max-w-[440px] mx-4 shadow-2xl cursor-default"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-5 text-white/60 hover:text-white text-2xl leading-none bg-transparent border-none cursor-pointer"
          aria-label="Close"
        >
          ×
        </button>
        <div className="text-center">
          <h2 className="text-white font-extrabold text-[24px] leading-tight mb-1">
            {t.contactUsTitle || "Contact Us"}
          </h2>
          <p className="text-[#D2C5E8] text-[14px] font-light">
            {t.contactUsDesc ||
              "Tell us about your organization and we'll get back to you."}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <input
              type="text"
              name="name"
              placeholder={t.contactUsNamePh || "Your Name"}
              value={form.name}
              onChange={handleChange}
              className="w-full h-[43px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-400 px-4"
            />
            {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
          </div>
          <div>
            <input
              type="email"
              name="email"
              placeholder={t.contactUsEmailPh || "Your Email"}
              value={form.email}
              onChange={handleChange}
              disabled={!!prefilledEmail}
              className={`w-full h-[43px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-400 px-4 ${prefilledEmail ? "opacity-70 cursor-not-allowed" : ""}`}
            />
            {errors.email && (
              <p className="text-red-400 text-sm mt-1">{errors.email}</p>
            )}
          </div>
          <div className="flex gap-2">
            <div className="w-[168px] flex-shrink-0 relative">
              <button
                type="button"
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                className="w-full h-[43px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium px-3 flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <Flag country={selectedCountry.country} size={20} />
                  <span>{selectedCountry.code}</span>
                </span>
                <span className="text-gray-400">▾</span>
              </button>
              {showCountryDropdown && (
                <div className="absolute top-full left-0 mt-1 min-w-[260px] w-max max-h-[200px] overflow-y-auto overflow-x-hidden bg-white rounded-[8px] shadow-lg z-50 border border-gray-200">
                  {countryCodes.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country.code)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 text-gray-800 flex items-center gap-2 text-sm whitespace-nowrap"
                    >
                      <Flag country={country.country} size={20} />
                      <span className="shrink-0">{country.code}</span>
                      <span className="text-gray-400 text-xs ml-1">
                        {country.label}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {errors.country_code && (
                <p className="text-red-400 text-sm mt-1">{errors.country_code}</p>
              )}
            </div>
            <div className="flex-1">
              <input
                type="tel"
                name="phone_no"
                placeholder="Phone Number"
                value={form.phone_no}
                onChange={handleChange}
                className={`w-full h-[43px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-400 px-4 ${errors.phone_no ? "border border-red-400" : ""}`}
              />
              {errors.phone_no && (
                <p className="text-red-400 text-sm mt-1">{errors.phone_no}</p>
              )}
            </div>
          </div>
          <div>
            <textarea
              name="note"
              placeholder={t.contactUsNotePh ||
                "Tell us about your team or custom needs…"}
              value={form.note}
              onChange={handleChange}
              rows={4}
              className="w-full bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-400 px-4 py-3 resize-none"
            />
            {errors.note && <p className="text-red-400 text-sm mt-1">{errors.note}</p>}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-[50px] bg-[#904DB0] hover:bg-[#7d3fa0] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-[87.5px] transition-colors text-[16px] flex items-center justify-center"
          >
            {isSubmitting ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              t.contactUsSend || "Send Request"
            )}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ContactUsPopup;
